import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest } from '../api/client'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'

const DEFAULT_RESOURCES = ['catalog','content','support','members','directors','quotes','finance','ai','system']

function CatalogSettings({ endpoint, title, description, actionLabel, itemLabel }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [editor, setEditor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminRequest(`/api/v1/admin/${endpoint}`)
      setItems(response?.data || [])
    } catch (nextError) {
      setError(nextError?.message || `${title}을 불러오지 못했습니다.`)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [endpoint])

  const rows = useMemo(() => items.filter((item) => !search || [item.name,item.id,item.status].join(' ').toLowerCase().includes(search.toLowerCase())), [items, search])
  const openEditor = (item = null) => setEditor(item ? { ...item, isNew: false } : { name:'',status:'ACTIVE',sortOrder:items.length,isNew:true })
  const save = async (event) => {
    event.preventDefault()
    if (!editor?.name?.trim()) return
    setNotice('')
    try {
      const body = { name: editor.name.trim(), status: editor.status, sortOrder: Number(editor.sortOrder || 0) }
      if (editor.isNew) await adminRequest(`/api/v1/admin/${endpoint}`, { method:'POST', body })
      else await adminRequest(`/api/v1/admin/${endpoint}/${encodeURIComponent(editor.id)}`, { method:'PUT', headers:{'If-Match':String(editor.version)}, body })
      setEditor(null)
      setNotice(`${itemLabel}을 실제 DB에 저장했습니다.`)
      await load()
    } catch (nextError) {
      setNotice(nextError?.message || '저장에 실패했습니다.')
    }
  }

  return <>
    <PageTitle eyebrow="SERVICE SETTINGS · LIVE DB" title={title} description={description} actions={<button className="button primary" onClick={() => openEditor()}>{actionLabel}</button>} />
    <div className="policy-callout"><strong>difan_dev 공통 기준</strong><span>사용자·디렉터·관리자 API가 동일한 {itemLabel} ID를 사용합니다. 브라우저 localStorage 시드에는 저장하지 않습니다.</span></div>
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <Card className="table-card"><div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder={`${title} 검색`} /></div><div className="table-result-bar"><span>총 <strong>{rows.length}</strong>건</span><small>source: PostgreSQL difan_dev</small></div>{loading ? <p className="help-copy">실제 데이터를 불러오는 중입니다.</p> : error ? <EmptyState title={`${title} 조회 실패`} description={error} /> : rows.length ? <div className="table-scroll"><table className="data-table compact"><thead><tr><th>{itemLabel}</th><th>노출 순서</th><th>Version</th><th>상태</th><th>수정일</th><th>관리</th></tr></thead><tbody>{rows.map((item) => <tr key={item.id}><td><strong>{item.name}</strong><small className="mono">{item.id}</small></td><td>{item.sortOrder}</td><td>v{item.version}</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.updatedAt, true)}</td><td><button className="text-button" onClick={() => openEditor(item)}>수정</button></td></tr>)}</tbody></table></div> : <EmptyState title={`등록된 ${itemLabel}이 없습니다.`} />}</Card>
    <AdminFormModal open={Boolean(editor)} title={editor?.isNew ? actionLabel : `${itemLabel} 수정`} description="저장 즉시 실제 공통 DB 값이 변경됩니다." onClose={() => setEditor(null)} onSubmit={save} submitLabel="저장">
      {editor && <FormGrid><FormField label={`${itemLabel}명`} wide required><input required value={editor.name} onChange={(event) => setEditor({...editor,name:event.target.value})} /></FormField><FormField label="노출 순서"><input type="number" value={editor.sortOrder ?? 0} onChange={(event) => setEditor({...editor,sortOrder:event.target.value})} /></FormField><FormField label="상태"><select value={editor.status || 'ACTIVE'} onChange={(event) => setEditor({...editor,status:event.target.value})}><option value="ACTIVE">사용 중</option><option value="INACTIVE">사용 중지</option></select></FormField></FormGrid>}
    </AdminFormModal>
  </>
}

export function CategorySettings() {
  return <CatalogSettings endpoint="categories" title="서비스 카테고리" description="서비스 찾기·견적 요청·디렉터 서비스 연결에서 공통으로 사용하는 실제 카테고리입니다." actionLabel="카테고리 추가" itemLabel="카테고리" />
}
export function RegionSettings() {
  return <CatalogSettings endpoint="regions" title="지역 관리" description="디렉터 활동 지역과 견적 요청 필터에서 사용하는 실제 지역 기준입니다." actionLabel="지역 추가" itemLabel="지역" />
}
export function TagSettings() {
  return <CatalogSettings endpoint="tags" title="태그 관리" description="디렉터·포트폴리오 검색과 추천에서 사용하는 실제 태그 사전입니다." actionLabel="태그 추가" itemLabel="태그" />
}

function roleLabel(role) {
  return { SUPER_ADMIN:'전체 관리자',OPERATIONS:'운영',CONTENT_MANAGER:'콘텐츠',SUPPORT:'고객지원',FINANCE:'결제·정산' }[role] || role
}

function permissionResources(item) {
  return [...new Set([...DEFAULT_RESOURCES, ...(item.permissions || []).map((permission) => permission.resource)])]
}

function normalizedPermissionList(item) {
  const byResource = new Map((item.permissions || []).map((permission) => [permission.resource, permission]))
  return permissionResources(item).map((resource) => ({ resource, view:false, export:false, edit:false, process:false, ...(byResource.get(resource) || {}) }))
}

export function AdminAccounts() {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [editor, setEditor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminRequest('/api/v1/admin/admin-accounts')
      setItems(response?.data || [])
    } catch (nextError) {
      setError(nextError?.message || '관리자 계정을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const rows = useMemo(() => items.filter((item) => !search || [item.loginId,item.email,item.role,item.status].join(' ').toLowerCase().includes(search.toLowerCase())), [items, search])
  const edit = (item) => setEditor({ ...item, permissions: normalizedPermissionList(item) })
  const setPermission = (resource, key, value) => setEditor((current) => ({ ...current, permissions: current.permissions.map((permission) => permission.resource === resource ? { ...permission, [key]:value } : permission) }))
  const save = async (event) => {
    event.preventDefault()
    setNotice('')
    try {
      await adminRequest(`/api/v1/admin/admin-accounts/${encodeURIComponent(editor.id)}`, { method:'PUT', body:{ role:editor.role,status:editor.status,permissions:editor.permissions } })
      setEditor(null)
      setNotice('관리자 계정·권한을 실제 DB에 저장했습니다. 비활성화된 계정의 세션은 즉시 폐기됩니다.')
      await load()
    } catch (nextError) {
      setNotice(nextError?.message || '관리자 권한 저장에 실패했습니다.')
    }
  }

  return <>
    <PageTitle eyebrow="ADMIN / RBAC · LIVE DB" title="관리자 계정·권한" description="admin_accounts와 admin_permissions의 실제 계정·권한을 관리합니다." />
    <div className="policy-callout"><strong>가짜 관리자 초대 기능 제거</strong><span>실제 초대 메일·초기 비밀번호 발급 API가 없는 상태에서 브라우저에 초대 계정을 만들지 않습니다. 현재는 존재하는 관리자 계정과 권한만 실제 DB에서 관리합니다.</span></div>
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <Card className="table-card"><div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder="관리자 ID, 이메일, 역할 검색" /></div><div className="table-result-bar"><span>관리자 <strong>{rows.length}</strong>명</span><small>source: admin_accounts</small></div>{loading ? <p className="help-copy">실제 관리자 계정을 불러오는 중입니다.</p> : error ? <EmptyState title="관리자 계정 조회 실패" description={error} /> : <div className="table-scroll"><table className="data-table"><thead><tr><th>관리자 ID</th><th>이메일</th><th>역할</th><th>권한 리소스</th><th>상태</th><th>최근 세션 사용</th><th>관리</th></tr></thead><tbody>{rows.map((item) => <tr key={item.id}><td><strong>{item.loginId || '-'}</strong><small className="mono">{item.id}</small></td><td>{item.email}</td><td>{roleLabel(item.role)}<small>{item.role}</small></td><td>{(item.permissions || []).length}개</td><td><StatusBadge status={item.status} /></td><td>{item.lastLoginAt ? formatDate(item.lastLoginAt, true) : '-'}</td><td><button className="text-button" onClick={() => edit(item)}>권한 수정</button></td></tr>)}</tbody></table></div>}</Card>
    <AdminFormModal open={Boolean(editor)} title="관리자 계정·권한 수정" description="저장 시 실제 admin_permissions를 교체합니다." onClose={() => setEditor(null)} onSubmit={save} submitLabel="실제 권한 저장" size="large">
      {editor && <><FormGrid><FormField label="관리자 ID" wide><input disabled value={editor.loginId || ''} /></FormField><FormField label="이메일" wide><input disabled value={editor.email || ''} /></FormField><FormField label="역할"><select value={editor.role} onChange={(event) => setEditor({...editor,role:event.target.value})}><option value="SUPER_ADMIN">SUPER_ADMIN</option><option value="OPERATIONS">OPERATIONS</option><option value="CONTENT_MANAGER">CONTENT_MANAGER</option><option value="SUPPORT">SUPPORT</option><option value="FINANCE">FINANCE</option></select></FormField><FormField label="상태"><select value={editor.status} onChange={(event) => setEditor({...editor,status:event.target.value})}><option value="ACTIVE">ACTIVE</option><option value="INACTIVE">INACTIVE</option></select></FormField></FormGrid><div className="table-scroll" style={{marginTop:16}}><table className="data-table compact"><thead><tr><th>Resource</th><th>조회</th><th>다운로드</th><th>등록·수정</th><th>승인·처리</th></tr></thead><tbody>{editor.permissions.map((permission) => <tr key={permission.resource}><td><strong>{permission.resource}</strong></td>{['view','export','edit','process'].map((key) => <td key={key}><input type="checkbox" checked={Boolean(permission[key])} onChange={(event) => setPermission(permission.resource,key,event.target.checked)} /></td>)}</tr>)}</tbody></table></div></>}
    </AdminFormModal>
  </>
}

export function AuditLogs() {
  return <AuditLogInline />
}

function AuditLogInline() {
  const [items,setItems]=useState([])
  useEffect(()=>{adminRequest('/api/v1/admin/audit-logs').then((response)=>setItems(response?.data||[])).catch(()=>setItems([]))},[])
  return <><PageTitle eyebrow="AUDIT LOG · LIVE DB" title="감사 로그" description="admin_audit_logs 실제 원장을 조회합니다." /><Card className="table-card"><div className="table-scroll"><table className="data-table"><thead><tr><th>시각</th><th>관리자</th><th>행위</th><th>대상</th><th>사유</th><th>요청 ID</th></tr></thead><tbody>{items.map((item)=><tr key={item.id}><td>{formatDate(item.createdAt,true)}</td><td>{item.admin}</td><td><small className="mono">{item.action}</small></td><td>{item.targetType}<small>{item.targetId}</small></td><td>{item.reason||'-'}</td><td><small className="mono">{item.requestId||'-'}</small></td></tr>)}</tbody></table></div></Card></>
}
